import './CustomerInsights.scss';
import cn from 'classnames';
import { format } from 'date-fns';
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { MdOutlineFileDownload, MdOutlineTableRows, MdShowChart } from 'react-icons/md';
import {
  KnowYourCustomerData,
  KnowYourCustomerSegmentRow
} from '@blisspointmedia/bpm-types/dist/KnowYourCustomer';
import { Background, Design, Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown/Dropdown';
import { DropdownOption } from '@Components/Dropdown/dropdown-constants';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Page } from '@Components/Page';
import { PAGE_STATE } from '@Components/Page/page-constants';
import { IconSegmentedControlButton } from '@Components/SegmentedControlButton';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { useSetError } from '../../redux/modals';
import { downloadJSONToCSV } from '../../utils/download-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useTabbedNav } from '../../utils/hooks/useNav';
import {
  CUSTOMER_INSIGHTS_LEVELS_PRETTY_NAMES,
  CUSTOMER_INSIGHTS_ORDERED_GROUPS,
  CUSTOMER_INSIGHTS_PRETTY_NAMES,
  CustomerInsightsFields,
  NAVS,
  SORT_DROPDOWN_OPTIONS,
  SortOptions,
  TabKey,
  ViewOptions
} from './customer-insights-constants';
import { CustomerInsightsGeoGroup } from './CustomerInsightsGeoGroup';
import { CustomerInsightsGroup } from './CustomerInsightsGroup';
import { CustomerInsightsHeader } from './CustomerInsightsHeader';
import {
  useKnowYourCustomerSegmentData,
  useKnowYourCustomerSegments
} from './useCustomerInsightsData';

const ACTION_STYLES = {
  design: Design.NEUTRAL,
  background: Background.DARK,
  size: Size.LG,
  stretch: StretchMode.FIT
};

const DROPDOWN_ACTION_STYLES = {
  size: Size.LG,
  stretch: StretchMode.FIT
};

interface MatchRate {
  matchedPopulation: number;
  totalPopulation: number;
  fraction: number;
}

export const CustomerInsights = () => {
  const { tab, goToTab } = useTabbedNav({
    baseURL: 'customer-insights',
    defaultKey: TabKey.CUSTOMER_BASE,
    navs: NAVS.map(nav => nav.key)
  });
  const { cid, name } = useCompanyInfo();
  const companyName = cid === 'tinuititest' ? 'Tinuiti' : name;
  const setError = useSetError();
  const [segmentValue, setSegmentValue] = useState<string>();
  const [sortValue, setSortValue] = useState<SortOptions>(SortOptions.ALPHABETICAL);
  const [graphView, setGraphView] = useState<ViewOptions>(ViewOptions.SHOW_GRAPH);
  const [suppressUnknown, setSuppressUnknown] = useState(true);

  const {
    data: allSegments,
    isLoading: segmentsLoading,
    error: segmentsError
  } = useKnowYourCustomerSegments(cid);

  useEffect(() => {
    if (!segmentValue && allSegments && allSegments.length > 0) {
      setSegmentValue(allSegments[0].segment_name);
    }
  }, [allSegments, segmentValue]);

  useEffect(() => {
    if (segmentsError) {
      setError({ message: segmentsError.message, reportError: segmentsError });
    }
  }, [segmentsError, setError]);

  useEffect(() => {
    if (allSegments && allSegments.length === 0) {
      const message = 'No default Know Your Customer segment found';
      setError({ message, reportError: new Error(message) });
    }
  }, [allSegments, setError]);

  const currentSegmentId = useMemo(
    () => allSegments?.find(segment => segment.segment_name === segmentValue)?.id,
    [allSegments, segmentValue]
  );

  const {
    data: segmentData,
    isLoading: segmentDataLoading,
    isPlaceholderData: segmentDataIsPlaceholder,
    error: segmentDataError
  } = useKnowYourCustomerSegmentData(currentSegmentId);

  useEffect(() => {
    if (segmentDataError) {
      setError({ message: segmentDataError.message, reportError: segmentDataError });
    }
  }, [segmentDataError, setError]);

  const { data, matchRate, lastUpdated } = useMemo(() => {
    if (!segmentData) {
      return { data: undefined, matchRate: undefined, lastUpdated: undefined };
    }

    const groupedRows = segmentData.rows.reduce<Record<string, KnowYourCustomerData[]>>(
      (acc, row) => {
        (acc[row.fieldName] ??= []).push(row);
        return acc;
      },
      {}
    );

    let derivedMatchRate: MatchRate | undefined;
    const matchRateRows = groupedRows[CustomerInsightsFields.MATCH_RATE];
    if (matchRateRows && matchRateRows.length > 0) {
      const matchRateRow = matchRateRows[0];
      derivedMatchRate = {
        matchedPopulation: matchRateRow.clientPopulation,
        totalPopulation: Math.round(
          matchRateRow.clientPopulation / matchRateRow.clientPopulationFraction
        ),
        fraction: matchRateRow.clientPopulationFraction
      };
    }

    delete groupedRows[''];
    return {
      data: groupedRows,
      matchRate: derivedMatchRate,
      lastUpdated: segmentData.lastUpdated
    };
  }, [segmentData]);

  const fetching = segmentsLoading || segmentDataLoading;

  const unknownSuppressedData: Record<string, KnowYourCustomerData[]> = useMemo(() => {
    if (!data) {
      return {};
    }

    const modifiedData = structuredClone(data);

    Object.keys(modifiedData).forEach(key => {
      const groups = modifiedData[key];
      const unknownIndex = groups.findIndex(group => group.levels === 'Unknown');

      if (unknownIndex !== -1) {
        groups.splice(unknownIndex, 1);

        const clientPopSum = groups.reduce((acc, group) => acc + group.clientPopulation, 0);
        const tuPopSum = groups.reduce((acc, group) => acc + group.tuPopulation, 0);
        modifiedData[key] = groups.map(group => ({
          ...group,
          clientPopulationFraction: group.clientPopulation / clientPopSum,
          tuPopulationFraction: group.tuPopulation / tuPopSum
        }));
      }
    });

    return modifiedData;
  }, [data]);

  const activeData = suppressUnknown ? unknownSuppressedData : data;

  const bodyContentByTab: Record<TabKey, ReactNode> = useMemo(
    () =>
      Object.values(TabKey).reduce(
        (acc, tabKey) => ({
          ...acc,
          [tabKey]: (() => {
            if (!activeData) {
              return null;
            }

            return (
              <CustomerInsightsBody
                tabKey={tabKey}
                matchRate={matchRate}
                activeData={activeData}
                companyName={companyName}
                sortValue={sortValue}
                graphView={graphView}
                suppressUnknown={suppressUnknown}
              />
            );
          })()
        }),
        {} as Record<TabKey, ReactNode>
      ),
    [activeData, companyName, graphView, matchRate, sortValue, suppressUnknown]
  );

  const actions = useMemo(
    () =>
      data &&
      lastUpdated &&
      segmentValue &&
      allSegments && (
        <CustomerInsightsActions
          data={data}
          companyName={companyName}
          lastUpdated={lastUpdated}
          isRefetching={segmentDataIsPlaceholder}
          segmentValue={segmentValue}
          allSegments={allSegments}
          setSegmentValue={setSegmentValue}
          suppressUnknown={suppressUnknown}
          setSuppressUnknown={setSuppressUnknown}
          sortValue={sortValue}
          setSortValue={setSortValue}
          graphView={graphView}
          setGraphView={setGraphView}
        />
      ),
    [
      allSegments,
      companyName,
      data,
      graphView,
      lastUpdated,
      segmentDataIsPlaceholder,
      segmentValue,
      sortValue,
      suppressUnknown
    ]
  );

  return (
    <Page
      title='Customer Insights'
      pageType='Customer Insights'
      state={(!data && fetching) || segmentDataIsPlaceholder ? PAGE_STATE.LOADING : undefined}
      navs={NAVS}
      selectedNav={tab}
      onNav={key => goToTab(key as TabKey)}
      actions={actions}
    >
      {!data && !fetching && (
        <div data-testid='not-configured-message'>
          This client has not yet been set up for Customer Insights.
        </div>
      )}
      {data &&
        !segmentDataIsPlaceholder &&
        Object.values(TabKey).map(tabKey => (
          <div
            className={cn('customer-insights-body', { hidden: tabKey !== tab })}
            aria-hidden={tabKey !== tab}
            key={tabKey}
            id={`${tabKey}-body`}
          >
            {bodyContentByTab[tabKey]}
          </div>
        ))}
    </Page>
  );
};

interface CustomerInsightsActionsProps {
  data: Record<string, KnowYourCustomerData[]>;
  companyName: string;
  lastUpdated: Date;
  isRefetching: boolean;
  segmentValue: string;
  allSegments: KnowYourCustomerSegmentRow[];
  setSegmentValue: Dispatch<SetStateAction<string | undefined>>;
  suppressUnknown: boolean;
  setSuppressUnknown: Dispatch<SetStateAction<boolean>>;
  sortValue: SortOptions;
  setSortValue: Dispatch<SetStateAction<SortOptions>>;
  graphView: ViewOptions;
  setGraphView: Dispatch<SetStateAction<ViewOptions>>;
}

const CustomerInsightsActions = ({
  data,
  companyName,
  lastUpdated,
  isRefetching,
  segmentValue,
  allSegments,
  setSegmentValue,
  suppressUnknown,
  setSuppressUnknown,
  sortValue,
  setSortValue,
  graphView,
  setGraphView
}: CustomerInsightsActionsProps) => {
  const handleDownload = () => {
    const splicedDataKeys = Object.keys(data).filter(
      key => key !== CustomerInsightsFields.MATCH_RATE
    );

    const formattedData = splicedDataKeys.flatMap(fieldName => {
      const rows = data[fieldName];
      const fieldPrettyName: string = CUSTOMER_INSIGHTS_PRETTY_NAMES[fieldName];

      return rows.map(row => {
        const levelsPrettyName: string =
          CUSTOMER_INSIGHTS_LEVELS_PRETTY_NAMES[fieldName][row.levels] ?? row.levels;

        return {
          'Demographic Category': fieldPrettyName,
          'Demographic Group': levelsPrettyName,
          [`${companyName}'s Population`]: Math.round(row.clientPopulation),
          [`${companyName}'s Distribution`]: row.clientPopulationFraction,
          'US Market': Math.round(row.tuPopulation),
          'US Market Distribution': row.tuPopulationFraction,
          Index: row.clientPopulationFraction / row.tuPopulationFraction,
          Penetration: row.clientPopulation / row.tuPopulation
        };
      });
    });

    const date = new Date();
    downloadJSONToCSV(formattedData, `CustomerInsights ${companyName} ${date.toLocaleString()}`);
  };

  const segmentOptions: DropdownOption[] = useMemo(
    () =>
      [...allSegments]
        .sort((a, b) => a.ordering - b.ordering)
        .map(row => ({
          value: row.segment_name,
          label: row.segment_name
        })),
    [allSegments]
  );

  return (
    <div className='customer-insights-actions'>
      <div className='last-updated'>
        {isRefetching ? '' : `Last Updated: ${format(lastUpdated, 'M/d/yy h:mmaaa')}`}
      </div>
      {allSegments.length > 1 && (
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          {...DROPDOWN_ACTION_STYLES}
          value={segmentValue}
          label='Segment'
          options={segmentOptions}
          onChange={setSegmentValue}
        />
      )}
      <ToggleSwitch
        {...ACTION_STYLES}
        className='unknown-toggle'
        label='Hide Unknown'
        checked={suppressUnknown}
        onChange={() => setSuppressUnknown(prev => !prev)}
        infoTooltipText='When an email address is recognized in the identity graph, specific demographic characteristics (e.g., ethnicity) may not be known with high confidence. In these cases, a value of &acute;Unknown&acute; will be returned for that characteristic for that user. You can suppress these values by using the Hide &acute;Unknown&acute; button to make relative comparisons easier to visualize.'
      />
      <Dropdown
        overlayPlacement={Placement.RIGHT}
        {...DROPDOWN_ACTION_STYLES}
        value={sortValue}
        label='Sort by'
        options={SORT_DROPDOWN_OPTIONS}
        onChange={setSortValue}
      />
      <IconSegmentedControlButton
        {...ACTION_STYLES}
        tooltipPlacement={Placement.BOTTOM}
        options={[
          { key: ViewOptions.SHOW_TABLE, icon: <MdOutlineTableRows />, label: 'table view' },
          { key: ViewOptions.SHOW_GRAPH, icon: <MdShowChart />, label: 'graph view' }
        ]}
        selectedOption={graphView}
        onChange={key => setGraphView(key as ViewOptions)}
      />
      <IconButton {...ACTION_STYLES} onClick={handleDownload}>
        <MdOutlineFileDownload />
      </IconButton>
    </div>
  );
};

interface CustomerInsightsBodyProps {
  tabKey: TabKey;
  matchRate: MatchRate | undefined;
  activeData: Record<string, KnowYourCustomerData[]>;
  companyName: string;
  sortValue: SortOptions;
  graphView: ViewOptions;
  suppressUnknown: boolean;
}

const CustomerInsightsBody = ({
  tabKey,
  matchRate,
  activeData,
  companyName,
  sortValue,
  graphView,
  suppressUnknown
}: CustomerInsightsBodyProps) => (
  <>
    <CustomerInsightsHeader tab={tabKey} matchRate={matchRate} />
    <CustomerInsightsGroup
      dataTestId='customer-insights-group-household'
      fields={CUSTOMER_INSIGHTS_ORDERED_GROUPS.household}
      data={activeData}
      header={
        <>
          What types of households do {companyName} customers reside in?
          <InfoTooltip placement={Placement.RIGHT}>
            The total number of households represented below may not equal the number of emails
            ingested due to differing levels of identity resolution (individual, household, etc.)
            across attributes.
          </InfoTooltip>
        </>
      }
      sortValue={sortValue}
      graphView={graphView}
      tab={tabKey}
      suppressUnknown={suppressUnknown}
    />
    <CustomerInsightsGroup
      dataTestId='customer-insights-group-individual'
      fields={CUSTOMER_INSIGHTS_ORDERED_GROUPS.individual}
      data={activeData}
      header={`What types of individuals form ${companyName}'s customer base?`}
      sortValue={sortValue}
      graphView={graphView}
      tab={tabKey}
      suppressUnknown={suppressUnknown}
    />
    <CustomerInsightsGeoGroup
      dataTestId='customer-insights-group-geo'
      data={activeData[CustomerInsightsFields.MSA]}
      totalPopulation={matchRate?.totalPopulation ?? 0}
      header={`Where do ${companyName} customers reside?`}
      tab={tabKey}
      graphView={graphView}
    />
  </>
);
